import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  CreditCard as CardIcon,
  OpenInNew as OpenInNewIcon,
  AccountTree as WorkflowIcon,
  PlayCircle as ExecutionIcon,
  People as UsersIcon,
} from '@mui/icons-material';
import { subscriptionService } from '../services/subscription.service';
import { MySubscription, SubscriptionPlanPublic, SubscriptionUsage } from '../types/subscription.types';
import { enqueueSnackbar } from 'notistack';

const STATUS_COLOR: Record<string, 'success' | 'warning' | 'error' | 'default' | 'info'> = {
  active: 'success',
  trialing: 'info',
  past_due: 'warning',
  canceled: 'error',
  incomplete: 'warning',
  paused: 'default',
  free: 'default',
};

// Helper component for a single usage row
const UsageRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  used: number;
  limit: number | null;
}> = ({ icon, label, used, limit }) => {
  const pct = limit != null ? Math.min((used / limit) * 100, 100) : 0;
  const atLimit = limit != null && used >= limit;
  const nearLimit = limit != null && pct >= 80 && !atLimit;
  const color = atLimit ? 'error' : nearLimit ? 'warning' : 'primary';

  return (
    <Box mb={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={0.5}>
        <Box display="flex" alignItems="center" gap={1}>
          <Box color={`${color}.main`} display="flex">{icon}</Box>
          <Typography variant="body2" fontWeight={500}>{label}</Typography>
        </Box>
        <Typography variant="body2" color={atLimit ? 'error.main' : 'text.secondary'}>
          {used.toLocaleString()}
          {limit != null ? ` / ${limit.toLocaleString()}` : ' (unlimited)'}
        </Typography>
      </Box>
      {limit != null && (
        <Tooltip title={`${pct.toFixed(0)}% used`} placement="top">
          <LinearProgress
            variant="determinate"
            value={pct}
            color={color}
            sx={{ height: 6, borderRadius: 3 }}
          />
        </Tooltip>
      )}
    </Box>
  );
};

export const SubscriptionPage: React.FC = () => {
  const [mySub, setMySub] = useState<MySubscription | null>(null);
  const [plans, setPlans] = useState<SubscriptionPlanPublic[]>([]);
  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [subRes, plansRes, usageRes] = await Promise.all([
        subscriptionService.getMySubscription(),
        subscriptionService.getPublicPlans(),
        subscriptionService.getUsage(),
      ]);
      setMySub(subRes);
      setPlans(Array.isArray(plansRes) ? plansRes : []);
      setUsage(usageRes);
    } catch {
      setError('Failed to load subscription data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    const isCancelled = params.get('checkout') === 'cancel';

    // Clear query params immediately to avoid re-triggering
    window.history.replaceState({}, '', window.location.pathname);

    if (isCancelled) {
      enqueueSnackbar('Checkout cancelled.', { variant: 'info' });
      load();
      return;
    }

    if (sessionId) {
      // Stripe redirected back with a completed session — sync immediately
      setLoading(true);
      subscriptionService.verifyCheckout(sessionId)
        .then((updatedSub) => {
          setMySub(updatedSub);
          enqueueSnackbar('Subscription activated successfully!', { variant: 'success' });
        })
        .catch(() => {
          enqueueSnackbar('Payment received — refreshing subscription status…', { variant: 'info' });
        })
        .finally(() => {
          load(); // always re-fetch to show latest state
        });
      return;
    }

    load();
  }, [load]);

  const handleCheckout = async (planId: string) => {
    setActionLoading(planId);
    try {
      const base = window.location.origin + window.location.pathname;
      const { checkout_url } = await subscriptionService.createCheckoutSession(
        planId,
        `${base}?checkout=success`,
        `${base}?checkout=cancel`,
      );
      window.location.href = checkout_url;
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Could not start checkout. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const handlePortal = async () => {
    setActionLoading('portal');
    try {
      const { portal_url } = await subscriptionService.createPortalSession(
        window.location.origin + window.location.pathname,
      );
      window.location.href = portal_url;
    } catch (err: any) {
      const msg = err?.response?.data?.error || 'Could not open billing portal. Please try again.';
      enqueueSnackbar(msg, { variant: 'error' });
    } finally {
      setActionLoading(null);
    }
  };

  const currentPlanId = mySub?.plan?.id ?? null;
  const isActive = mySub?.status === 'active' || mySub?.status === 'trialing';

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box p={{ xs: 2, sm: 3 }} maxWidth={900} mx="auto">
      <Typography variant="h4" fontWeight={700} mb={1}>
        Subscription
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage your organisation's plan and billing.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {/* Current plan summary */}
      {mySub && (
        <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
          <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={1}>
            <Typography variant="h6" fontWeight={600}>
              {mySub.plan?.name ?? 'Free'}
            </Typography>
            <Chip
              label={(mySub.status ?? 'free').replace('_', ' ')}
              color={STATUS_COLOR[mySub.status ?? 'free'] ?? 'default'}
              size="small"
            />
          </Box>

          {mySub.plan && (
            <Typography variant="body2" color="text.secondary">
              ${parseFloat(mySub.plan.price_per_month).toFixed(2)} / month
            </Typography>
          )}

          {mySub.current_period_end && (
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {mySub.cancel_at_period_end ? 'Cancels' : 'Renews'} on{' '}
              {new Date(mySub.current_period_end).toLocaleDateString()}
            </Typography>
          )}

          {isActive && mySub?.has_billing_portal && (
            <Box mt={2}>
              <Button
                variant="outlined"
                size="small"
                startIcon={actionLoading === 'portal' ? <CircularProgress size={14} /> : <OpenInNewIcon />}
                onClick={handlePortal}
                disabled={actionLoading === 'portal'}
              >
                Manage Billing
              </Button>
            </Box>
          )}
        </Paper>
      )}

      {/* Usage summary — only show when there's an active subscription */}
      {usage && (
        <Paper variant="outlined" sx={{ p: 2, mb: 4, borderRadius: 2 }}>
          <Typography variant="subtitle1" fontWeight={600} mb={2}>
            Your Usage This Month
          </Typography>

          {!usage.is_active && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Your subscription is not active. Workflow executions and creation are currently blocked.
            </Alert>
          )}

          <UsageRow
            icon={<WorkflowIcon fontSize="small" />}
            label="Active Workflows"
            used={usage.workflows.used}
            limit={usage.workflows.limit}
          />
          <UsageRow
            icon={<ExecutionIcon fontSize="small" />}
            label="Executions this month"
            used={usage.executions_this_month.used}
            limit={usage.executions_this_month.limit}
          />
          <UsageRow
            icon={<UsersIcon fontSize="small" />}
            label="Active Users"
            used={usage.users.used}
            limit={usage.users.limit}
          />

          {/* Limit warning alerts */}
          {usage.is_active && usage.workflows.limit != null && usage.workflows.used >= usage.workflows.limit && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Workflow limit reached — you cannot create new workflows until you upgrade your plan.
            </Alert>
          )}
          {usage.is_active && usage.executions_this_month.limit != null && usage.executions_this_month.used >= usage.executions_this_month.limit && (
            <Alert severity="error" sx={{ mt: 1 }}>
              Monthly execution limit reached — executions are blocked until next month or you upgrade.
            </Alert>
          )}
          {usage.is_active && usage.users.limit != null && usage.users.used >= usage.users.limit && (
            <Alert severity="error" sx={{ mt: 1 }}>
              User limit reached — you cannot add more users until you upgrade your plan.
            </Alert>
          )}
        </Paper>
      )}

      {/* Available plans */}
      {plans.length > 0 && (
        <>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Available Plans
          </Typography>
          <Grid container spacing={2}>
            {[...plans].sort((a, b) => a.sort_order - b.sort_order).map((plan) => {
              const isCurrent = plan.id === currentPlanId;
              const isLoadingThis = actionLoading === plan.id;
              return (
                <Grid item xs={12} sm={6} md={4} key={plan.id}>
                  <Card
                    variant="outlined"
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      borderColor: isCurrent ? 'primary.main' : undefined,
                      borderWidth: isCurrent ? 2 : 1,
                    }}
                  >
                    <CardContent sx={{ flexGrow: 1 }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" fontWeight={600}>{plan.name}</Typography>
                        {isCurrent && <Chip label="Current" color="primary" size="small" />}
                      </Box>
                      <Typography variant="h5" fontWeight={700} color="primary" mb={1}>
                        ${parseFloat(plan.price_per_month).toFixed(2)}
                        <Typography component="span" variant="body2" color="text.secondary">
                          {' '}/ mo
                        </Typography>
                      </Typography>
                      {plan.description && (
                        <Typography variant="body2" color="text.secondary" mb={1}>
                          {plan.description}
                        </Typography>
                      )}
                      <Divider sx={{ my: 1 }} />
                      <List dense disablePadding>
                        {plan.max_workflows != null && (
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary={`${plan.max_workflows} workflows`} />
                          </ListItem>
                        )}
                        {plan.max_executions_per_month != null && (
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary={`${plan.max_executions_per_month.toLocaleString()} executions/mo`} />
                          </ListItem>
                        )}
                        {plan.max_users != null && (
                          <ListItem disableGutters>
                            <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary={`${plan.max_users} users`} />
                          </ListItem>
                        )}
                        {plan.features?.map((f, i) => (
                          <ListItem key={i} disableGutters>
                            <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                            <ListItemText primary={f} />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                    <CardActions sx={{ p: 2, pt: 0 }}>
                      <Button
                        fullWidth
                        variant={isCurrent ? 'outlined' : 'contained'}
                        disabled={isCurrent || !!actionLoading}
                        startIcon={isLoadingThis ? <CircularProgress size={14} /> : <CardIcon />}
                        onClick={() => handleCheckout(plan.id)}
                      >
                        {isCurrent ? 'Current Plan' : 'Subscribe'}
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </>
      )}

      {plans.length === 0 && !loading && (
        <Alert severity="info">No plans are currently available. Please check back later.</Alert>
      )}
    </Box>
  );
};
